import {useCallback, useRef, useState} from 'react';
import {Navigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Camera} from 'lucide-react';
import {displayError} from '@chekin/api';
import {IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE, getFeatureFlags} from '@chekin/core';
import {useSummary} from '../../../../context/summary';
import {useDocumentSelfieDetection} from 'hooks';
import {isUnattendedTerminal} from 'hooks/terminalMode';
import {
  blurSelfiePhoto,
  CameraCapture,
  PORTRAIT_FACE_FRAME,
  useIVStore,
  type CameraCaptureControls,
} from '@chekin/shared';
import {SelfiePrepGuide} from '@chekin/shared/guest-form';
import selfieSilhouette from '@chekin/shared/images/selfie-silhouette.webp?url';
import {PATHS} from 'routes/constants';
import {cn} from '@chekinapp/ui';
import {PageLayout} from 'components/PageLayout';
import {SkipForNow} from 'components/common/SkipForNow';
import {RegistrationStepSubtitle} from 'components/RegistrationProgress';
import {CaptureHints} from '../../components/CaptureHints';
import {useDiscardLastPhotoBack} from 'hooks/iv/useDiscardLastPhotoBack';
import {
  getIVCaptureChecks,
  useIVCaptureScreenText,
  useIVCaptureKicker,
} from '../../ivCaptureCopy';

const videoConstraints = {
  facingMode: 'user',
};

function SelfieScanView() {
  const {id} = useParams();
  const {t} = useTranslation();
  const {t: tIV} = useTranslation('identity-verification');
  const {onBack: openDiscardModal, modal: discardModal} = useDiscardLastPhotoBack();
  const {reservationId, countryCode} = useSummary();
  const docType = IV_DOCUMENT_IMAGE_TYPES.selfie;
  const isGuided = !isUnattendedTerminal();
  const captureRef = useRef<CameraCaptureControls>(null);
  const [isIntroVisible, setIsIntroVisible] = useState(false);

  const frontPhoto = useIVStore(state => state.imagesDetails.front);

  const {
    isProcessing,
    processState,
    passedChecks,
    errorMessage,
    errorDescription,
    errorReason,
    isServiceFailure,
    failureCount,
    onTryAgain,
    onVerifyLater,
    canVerifyLater,
    isFinalStep,
    biomatch,
    sendSelfieToDetectFace,
  } = useDocumentSelfieDetection({docType});

  const [autoCaptureUnavailable, setAutoCaptureUnavailable] = useState(false);

  const handleBack = () => {
    if (!isProcessing && captureRef.current?.showIntro()) return;
    openDiscardModal();
  };

  const {copy, title, intro, isChecking} = useIVCaptureScreenText(
    'selfie',
    processState,
    isProcessing,
    {
      autoCaptureUnavailable,
      presentation: 'ring',
      isIntro: isIntroVisible,
      isFinalStep,
      biomatch,
      errorReason,
      failureCount,
    },
  );

  const kicker = useIVCaptureKicker(copy.kicker);
  const fillsPage = Boolean(isProcessing);

  const handleChangePhoto = useCallback(
    async ({image, source}: {image: string; source: IV_PHOTO_SOURCE}) => {
      sendSelfieToDetectFace({source, countryCode, reservationId, image});

      try {
        return await blurSelfiePhoto({photo: image});
      } catch (error) {
        displayError(error as string);
      }
    },
    [countryCode, reservationId, sendSelfieToDetectFace],
  );

  if (!frontPhoto.uploaded) {
    return <Navigate to={`../../${PATHS.verification.main}/${id ?? ''}`} />;
  }

  return (
    <PageLayout
      kicker={kicker}
      className="iv-selfie-view"
      fillViewport={fillsPage}
      title={title}
      intro={intro}
      align={isChecking ? 'center' : undefined}
      backLabel={t('back')}
      onBack={handleBack}
      titleAction={isChecking ? undefined : <SkipForNow />}
      mobileTitle={t('guest_registration_title')}
      mobileSubtitle={<RegistrationStepSubtitle />}
    >
      <div className={cn('flex flex-col gap-8', fillsPage && 'flex-1')}>
        <CameraCapture
          className={cn(
            'ph-no-capture [--capture-view-gradient-face:var(--capture-view-gradient)]',
            PORTRAIT_FACE_FRAME,
          )}
          variant="face"
          controlRef={captureRef}
          autoCapture={getFeatureFlags('IV_SELFIE_AUTO_CAPTURE')}
          allowUpload={false}
          allowDeviceCamera={!isUnattendedTerminal()}
          additionalConstraints={videoConstraints}
          intro={isGuided ? <SelfiePrepGuide /> : undefined}
          onIntroVisibilityChange={setIsIntroVisible}
          introLabel={
            <>
              <Camera />
              {tIV('open_camera')}
            </>
          }
          checking={{checks: getIVCaptureChecks('selfie'), passedChecks, isFinalStep}}
          faceGuideSize="max"
          faceSilhouette={<img src={selfieSilhouette} alt="" />}
          pendingStatusLabel={tIV('turning_on_camera')}
          viewfinderHint={tIV('look_straight_even_light')}
          shutterLabel={tIV('take_photo')}
          onCapture={handleChangePhoto}
          onAutoCaptureUnavailable={() => setAutoCaptureUnavailable(true)}
          processState={isProcessing ? processState : undefined}
          statusLabel={tIV('keep_face_inside_oval')}
          scanningLabel={copy.scanningLabel}
          successLabel={copy.successLabel}
          errorTitle={errorMessage || copy.errorTitle}
          errorDescription={errorDescription || copy.errorDescription}
          hasKnownCause={Boolean(errorReason)}
          canResend={isServiceFailure}
          hints={
            isIntroVisible ? undefined : (
              <CaptureHints variant="face" className="lg:hidden" />
            )
          }
          onRetry={onTryAgain}
          onVerifyLater={canVerifyLater ? onVerifyLater : undefined}
        />
      </div>
      {discardModal}
    </PageLayout>
  );
}

export {SelfieScanView};
